import { useMemo, useCallback } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getThreadsQuery } from '@ts-core/graphql/forum/queries';

import {
  createThreadMutation,
  deleteThreadMutation,
  upvoteThreadMutation,
  downvoteThreadMutation,
  reportThreadMutation,
} from '@ts-core/graphql/forum/mutations';

import { useMutationCompletionHandler } from '@ts-core/lib/apollo';

export default function useThreads({ courseId, skipFetch = false, onThreadCreated, onThreadDeleted }) {
  const { removeItemFromCache, onError, onCompleted, addItemToCache } = useMutationCompletionHandler();

  const { data, loading } = useQuery(getThreadsQuery, {
    variables: { courseId },
    skip: skipFetch || !courseId,
    fetchPolicy: 'cache-and-network',
  });

  const threads = useMemo(() => data?.threads || [], [data]);

  /**
   * Create Thread
   */
  const [createThread, { loading: creatingThread }] = useMutation(createThreadMutation, {
    onError,
    onCompleted: onThreadCreated,
    update: addItemToCache({
      query: getThreadsQuery,
      queryField: 'threads',
      variables: { courseId },
      mutationField: 'createThread',
    }),
  });
  const onCreateThread = useCallback(
    (input) => {
      createThread({
        variables: {
          courseId,
          input,
        },
      });
    },
    [createThread, courseId]
  );

  /**
   * Delete Thread
   */
  const [deleteThread] = useMutation(deleteThreadMutation, {
    onCompleted: onThreadDeleted,
    onError,
    update: removeItemFromCache({
      query: getThreadsQuery,
      queryField: 'threads',
      variables: { courseId },
      mutationField: 'deleteThread',
    }),
  });
  const onDeleteThread = useCallback(
    (mThread) => {
      deleteThread({
        variables: {
          courseId,
          threadId: mThread._id,
        },
      });
    },
    [deleteThread, courseId]
  );

  /**
   * Upvote thread
   */
  const [upvoteThread] = useMutation(upvoteThreadMutation, {
    onError,
  });
  const onUpvoteThread = useCallback(
    (mThread) => {
      const updatedThread = {
        ...mThread,
        score: mThread.score + 1,
        canUpvote: false,
        canDownvote: false,
      };
      upvoteThread({
        variables: {
          courseId,
          threadId: mThread._id,
        },
        optimisticResponse: {
          upvoteThread: updatedThread,
        },
      });
    },
    [upvoteThread, courseId]
  );

  /**
   * Downvote thread
   */
  const [downvoteThread] = useMutation(downvoteThreadMutation, {
    onError,
  });
  const onDownvoteThread = useCallback(
    (mThread) => {
      const updatedThread = {
        ...mThread,
        score: mThread.score - 1,
        canUpvote: false,
        canDownvote: false,
      };
      downvoteThread({
        variables: {
          courseId,
          threadId: mThread._id,
        },
        optimisticResponse: {
          downvoteThread: updatedThread,
        },
      });
    },
    [downvoteThread, courseId]
  );

  /**
   * Report Thread
   */
  const [reportThread] = useMutation(reportThreadMutation, {
    onError,
    onCompleted,
  });
  const onReportThread = useCallback(
    (thread, input) => {
      reportThread({
        variables: {
          courseId,
          threadId: thread._id,
          input,
        },
      });
    },
    [reportThread, courseId]
  );

  return {
    threads,
    loading,

    onCreateThread,
    creatingThread,

    onDeleteThread,

    onUpvoteThread,
    onDownvoteThread,

    onReportThread,
  };
}
